import {
  collection,
  doc,
  getDocs,
  query,
  where,
  deleteDoc,
  DocumentReference,
} from 'firebase/firestore';
import { deleteObject, listAll, ref } from 'firebase/storage';
import { auth, db, storage } from '../services/firebase';
import { createTripNotifications } from '../services/notificationService';
import { TripConfig } from '../types';
import { tripAdminIds } from './tripRoles';
import { deleteDocumentsUnder } from './bookingDocuments';
import { respondToTrip } from '../services/tripMembership';
import { getPendingInvitations } from '../services/userLookup';

async function deleteAllDocsIn(parentRef: DocumentReference, subName: string) {
  const snap = await getDocs(collection(parentRef, subName));
  await Promise.all(snap.docs.map(d => deleteDoc(d.ref)));
}

/**
 * Deletes every uploaded photo under trips/{tripId}/memories/ in Storage.
 * Firestore's `memories` docs only hold a download URL pointing at these
 * objects — deleting the docs (deleteAllDocsIn above) never touched the
 * actual files, which is the "omits Storage" half of the 2026-09-22 finding
 * this closes. `listAll` rather than reading each doc's `imageUrl`, so this
 * also catches any object left behind by a memory whose Firestore doc was
 * already gone for some other reason (a prior failed delete, for instance) —
 * the goal is an empty prefix, not a mirror of current Firestore state.
 */
async function deleteTripMemoryPhotos(tripId: string) {
  const folderRef = ref(storage, `trips/${tripId}/memories`);
  const listing = await listAll(folderRef);
  await Promise.all(listing.items.map(item => deleteObject(item)));
}

/** Everything under a prefix that may not be deletable yet (the rules for it
 *  ship separately): a leftover file must never block deleting the trip. */
const bestEffortDeleteUnder = (prefix: string) =>
  deleteDocumentsUnder(storage, prefix).catch(err => console.warn(`Couldn't clear ${prefix}:`, err));

/** `uid` is the account doing the deleting — see the checklist note below. */
export async function deleteTripFully(tripId: string, uid: string) {
  const tripRef = doc(db, 'trips', tripId);
  await Promise.all([
    deleteAllDocsIn(tripRef, 'expenses'),
    deleteAllDocsIn(tripRef, 'payments'),
    deleteAllDocsIn(tripRef, 'memories'),
    deleteAllDocsIn(tripRef, 'itinerary'),
    // Per-destination generated schedules — trips/{tripId}/schedules/{destId}
    // (DestinationScheduler.tsx). Missing until 2026-09-22: a deleted trip's
    // schedule documents were orphaned forever, unreachable by any query path
    // once the parent trip doc was gone but never actually removed.
    deleteAllDocsIn(tripRef, 'schedules'),
    // The deployed cleanup trigger clears files after the trip doc is gone;
    // a client-side Storage failure must not prevent that trigger from firing.
    deleteTripMemoryPhotos(tripId).catch(err => console.warn("Couldn't clear trip memory photos:", err)),
    // Original confirmation PDFs/emails kept with imported bookings.
    bestEffortDeleteUnder(`trips/${tripId}/bookingDocs`),
  ]);
  // Only the caller's own checklist: each traveller's is private to them, so
  // listing the collection is denied even to the owner — which used to throw
  // here and abort account deletion before the trip doc went.
  // Other travellers' checklists (and anything else left) are cleared by the
  // cleanupDeletedTrip Function once the trip doc goes.
  await deleteOwnChecklistInTrip(tripId, uid);
  await deleteDoc(tripRef);
}

async function deleteOwnChecklistInTrip(tripId: string, uid: string) {
  const ref = doc(db, 'trips', tripId, 'userChecklists', uid);
  await deleteAllDocsIn(ref, 'items');
  await deleteDoc(ref).catch(() => {});
}

/**
 * Cascade-deletes the authenticated user's account across the database.
 *
 * Strategy:
 *   - Personal data (notifications, pendingImports, own checklists, /users doc) is HARD deleted.
 *   - Trips owned by the user with no other joined Trip Admin are HARD deleted
 *     (subcollections first); with one, the trip is handed to them instead. Every
 *     other member receives a notification: "[Trip name] trip was deleted by its creator".
 *   - Trips they were a non-owner member of: removed from memberIds/adminIds and
 *     their traveller entry is anonymised so historical expense/memory references
 *     still resolve to a name (rendered as "[Deleted user]").
 *   - Trips where they were only invited (in inviteEmails, never joined): their
 *     email and any placeholder traveller entry are stripped.
 *
 * Must be called while the user is authenticated as themselves — Firestore
 * rules enforce isOwner / isMember checks on every write below.
 */
export async function deleteAccountCascade(uid: string, email: string | null | undefined) {
  // 1. Discover every trip the user touches
  const ownedQ = query(collection(db, 'trips'), where('ownerId', '==', uid));
  const memberQ = query(collection(db, 'trips'), where('memberIds', 'array-contains', uid));
  const [ownedSnap, memberSnap, pendingInvitations] = await Promise.all([
    getDocs(ownedQ),
    getDocs(memberQ),
    email && auth.currentUser?.emailVerified ? getPendingInvitations() : Promise.resolve([]),
  ]);

  const handled = new Set<string>();

  // 2. Owned trips — delete entirely and notify every other member
  for (const td of ownedSnap.docs) {
    const trip = td.data() as TripConfig;
    // Admins are peers: if another joined admin is still on the trip, it's
    // theirs now, and this account leaves like any member (step 3 below)
    // with ownerId handed over in the same write.
    const heir = tripAdminIds(trip).find(id => id !== uid && (trip.memberIds || []).includes(id));
    if (heir && (trip.memberIds || []).includes(uid)) continue;
    handled.add(td.id);
    const others = (trip.memberIds || []).filter(id => id !== uid);

    // Notify other members before the trip document is gone. Routed through the
    // callable (which authorizes caller=owner + recipient=member server-side);
    // the trip still exists at this point, before deleteTripFully() runs.
    await createTripNotifications({
      tripId: td.id,
      recipientIds: others,
      title: 'Trip Deleted',
      message: `**${trip.title}** trip was deleted by its creator`,
      type: 'trip-deleted',
      category: 'trip-admin',
      priority: 'P1',
      data: { tripTitle: trip.title, creatorId: uid },
    });

    await deleteTripFully(td.id, uid);
  }

  // 3. Member-of (not owner) — strip from membership, anonymise
  for (const td of memberSnap.docs) {
    if (handled.has(td.id)) continue;
    handled.add(td.id);
    await respondToTrip(td.id, 'leave');
    await deleteOwnChecklistInTrip(td.id, uid);
  }

  // 4. Invited-only (in inviteEmails but never joined) — strip email + placeholder
  for (const invitation of pendingInvitations) {
    if (handled.has(invitation.tripId)) continue;
    handled.add(invitation.tripId);
    await respondToTrip(invitation.tripId, 'decline');
  }

  // 5. Personal subcollections + user doc
  const userRef = doc(db, 'users', uid);
  await Promise.all([
    deleteAllDocsIn(userRef, 'notifications'),
    deleteAllDocsIn(userRef, 'pendingImports'),
    // Documents kept from forwarded booking emails awaiting review.
    bestEffortDeleteUnder(`users/${uid}/importDocs`),
  ]);
  await deleteDoc(userRef);
}
